import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';

/**
 * Directive to enable horizontal scrolling with the mouse wheel.
 *
 * @description
 * This directive captures vertical scroll events (`wheel`) and converts them into horizontal
 * scrolling behavior on the target element. It manually registers a non-passive event listener
 * to allow `preventDefault()` and suppress browser performance warnings.
 *
 * ### Usage:
 * ```html
 * <div class="horizontal-scroll" appHorizontalScroll>
 *   <!-- horizontally overflowing content -->
 * </div>
 * ```
 *
 * @directive
 */
@Directive({
    selector: '[appHorizontalScroll]',
    standalone: false
})
export class HorizontalScrollDirective implements OnInit, OnDestroy {

  /**
   * Scroll speed multiplier applied to the vertical delta to calculate horizontal scroll distance.
   * A higher number means faster horizontal scrolling.
   *
   * @private
   * @readonly
   */
  private readonly scrollSpeed = 0.5;

  /**
   * Bound handler reference to ensure consistent removeEventListener binding.
   *
   * @private
   */
  private boundWheelHandler: (event: WheelEvent) => void;

  /**
   * Constructor that injects the reference to the host DOM element.
   *
   * @param elementRef - A reference to the DOM element the directive is applied to.
   */
  constructor(
    private elementRef: ElementRef,
  ) {
  }

  /**
   * Lifecycle hook that registers the `wheel` event listener on the host element.
   * The listener is registered as non-passive to allow calling `preventDefault()`.
   */
  ngOnInit(): void {
    this.boundWheelHandler = this.onWheelScroll.bind(this);
    this.elementRef.nativeElement.addEventListener('wheel', this.boundWheelHandler, {
      passive: false,
    });
  }

  /**
   * Lifecycle hook that removes the `wheel` event listener when the directive is destroyed.
   */
  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('wheel', this.boundWheelHandler);
  }

  /**
   * Handles the `wheel` event and converts vertical scroll to horizontal scroll.
   *
   * @param event - The `WheelEvent` triggered by user scrolling.
   */
  private onWheelScroll(event: WheelEvent): void {
    if (event.deltaY !== 0) {
      const scrollAmount = event.deltaY * this.scrollSpeed;
      this.elementRef.nativeElement.scrollLeft += scrollAmount;
      event.preventDefault();
    }
  }
}
